<template>
  <div>
    <CashflowSelect @updated="updated" />
    <div v-if="modifiedCashflow">
      <v-switch v-model="showRecurringEnded" label="Show recurring ended" />
      <h1>Cashflow</h1>
      <div>
        <div style="float: right;">
          <a @click="editAccount()">Add account</a>
          <span>&#160;|&#160;</span>
          <a @click="deleteCashflow()">Delete cashflow</a>
        </div>
        <strong>
          <a @click="editCashflow()">{{modifiedCashflow.name}}</a>
        </strong>
        <span v-if="modifiedCashflow.currency">{{` (${modifiedCashflow.currency.symbol})`}}</span>
      </div>
      <h2>Accounts</h2>
      <ol v-if="modifiedAccounts">
        <li v-for="account in modifiedAccounts" :key="account.id">
          <div style="float: right;">
            <a @click="editAccount(account)">Edit</a>
            <span>&#160;</span>
            <a @click="deleteAccount(account)">Delete</a>
          </div>
          <div>
            <h3>
              Account: {{account.name}}
              <span
                v-if="account.currency"
              >{{` (${account.currency.symbol})`}}</span>
            </h3>
          </div>
          <ul>
            <li>
              <div>
                <div style="float: right;">
                  <a @click="editBalance(account)">Add balance</a>
                </div>
                <h3>Balances</h3>
              </div>
              <v-data-table
                :headers="headersBalance"
                :items="account.balances"
                v-if="account.balances && account.balances.length"
                hide-actions
                must-sort
                class="elevation-1"
              >
                <template v-slot:items="props">
                  <tr>
                    <td>{{props.item.timestamp | formatDate}}</td>
                    <td
                      :class="`cashflowCell ${props.item.sign && props.item.sign.id == 1 ? 'cashflowNegative' : null}`"
                    >{{props.item.value | toCurrency}}</td>
                    <td style="text-align:right;">
                      <a @click="editBalance(account, props.item)">Edit</a>
                      <span>&#160;</span>
                      <a @click="deleteBalance(props.item)">Delete</a>
                    </td>
                  </tr>
                </template>
              </v-data-table>
            </li>
            <li>
              <div>
                <div style="float: right;">
                  <a @click="editRecurring(account)">Add recurring</a>
                </div>
                <h3>Recurring</h3>
              </div>
              <v-data-table
                :headers="headersRecurring"
                :items="account.recurrings"
                v-if="account.recurrings && account.recurrings.length"
                hide-actions
                must-sort
                class="elevation-1"
              >
                <template v-slot:items="props">
                  <tr>
                    <td>{{props.item.timestamp | formatDate}}</td>
                    <td>
                      <span v-if="props.item.end">{{props.item.end | formatDate}}</span>
                    </td>
                    <td
                      :class="`cashflowCell ${props.item.sign && props.item.sign.id == 1 ? 'cashflowNegative' : null}`"
                    >{{props.item.value | toCurrency}}</td>
                    <td>
                      <span v-if="props.item.purpose">{{props.item.purpose.body}}</span>
                      <span v-if="!props.item.purpose">{{props.item.purposeid}}</span>
                    </td>
                    <td>
                      <span
                        v-if="props.item.period"
                      >{{`Every ${props.item.nperiod} ${props.item.period.value}`}}</span>
                    </td>
                    <td>{{props.item.name}}</td>
                    <td style="text-align:right;">
                      <a @click="editRecurring(account, props.item)">Edit</a>
                      <span>&#160;</span>
                      <a @click="deleteRecurring(props.item)">Delete</a>
                    </td>
                  </tr>
                </template>
              </v-data-table>
            </li>
            <li>
              <div>
                <div style="float: right;">
                  <a @click="editTransaction(account)">Add transaction</a>
                </div>
                <h3>Transactions</h3>
              </div>
              <v-data-table
                :headers="headersTransaction"
                :items="account.transactions"
                v-if="account.transactions && account.transactions.length"
                hide-actions
                must-sort
                class="elevation-1"
              >
                <template v-slot:items="props">
                  <tr>
                    <td>{{props.item.timestamp | formatDate}}</td>
                    <td>
                      <span v-if="props.item.purpose">{{props.item.purpose.body}}</span>
                      <span v-if="!props.item.purpose">{{props.item.purposeid}}</span>
                    </td>
                    <td
                      :class="`cashflowCell ${props.item.sign && props.item.sign.id == 1 ? 'cashflowNegative' : null}`"
                    >{{props.item.value | toCurrency}}</td>
                    <td style="text-align:right;">
                      <a @click="editTransaction(account, props.item)">Edit</a>
                      <span>&#160;</span>
                      <a @click="deleteTransaction(props.item)">Delete</a>
                    </td>
                  </tr>
                </template>
              </v-data-table>
            </li>
          </ul>
        </li>
      </ol>
    </div>
    <v-dialog
      v-model="dialogCashflow"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
      v-if="cashflow"
    >
      <v-card>
        <v-toolbar dark color="primary">
          <v-btn icon dark @click="dialogCashflow = false">
            <v-icon>close</v-icon>
          </v-btn>
          <v-toolbar-title>Edit cashflow</v-toolbar-title>
        </v-toolbar>
        <v-card-text>
          <p>Edit this cashflow entry</p>
          <v-form ref="cashflowForm" v-model="validCashflow" lazy-validation>
            <v-text-field label="Name" v-model="cashflow.name" :rules="valueRules" outline />
            <v-select
              label="Currency"
              v-model.number="cashflow.currencyid"
              :items="currencies"
              item-value="id"
              item-text="name"
              :rules="valueRules"
              outline
              v-if="currencies"
            />
            <v-btn @click="saveCashflow()">Save</v-btn>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="dialogAccount"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
      v-if="account"
    >
      <v-card>
        <v-toolbar dark color="primary">
          <v-btn icon dark @click="dialogAccount = false">
            <v-icon>close</v-icon>
          </v-btn>
          <v-toolbar-title>Edit account</v-toolbar-title>
        </v-toolbar>
        <v-card-text>
          <p>Edit this account entry</p>
          <v-form ref="accountForm" v-model="validAccount" lazy-validation>
            <v-text-field label="Name" v-model="account.name" :rules="valueRules" outline />
            <v-select
              label="Currency"
              v-model.number="account.currencyid"
              :items="currencies"
              item-value="id"
              item-text="name"
              :rules="valueRules"
              outline
              v-if="currencies"
            />
            <v-btn @click="saveAccount()">Save</v-btn>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="dialogBalance"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
      v-if="balance"
    >
      <v-card>
        <v-toolbar dark color="primary">
          <v-btn icon dark @click="dialogBalance = false">
            <v-icon>close</v-icon>
          </v-btn>
          <v-toolbar-title>Edit balance</v-toolbar-title>
        </v-toolbar>
        <v-card-text>
          <p>Edit this balance point entry</p>
          <v-form ref="balanceForm" v-model="validBalance" lazy-validation>
            <DateDropdownHint label="Date" v-model="balance.timestamp" />
            <v-select
              label="Sign"
              v-model.number="balance.signid"
              :items="signs"
              item-value="id"
              item-text="value"
              outline
              v-if="signs"
            />
            <v-text-field
              type="number"
              label="Value"
              v-model.number="balance.value"
              :rules="valueRules"
              outline
            />
            <v-btn @click="saveBalance()">
              <v-icon>save</v-icon>Save
            </v-btn>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="dialogRecurring"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
      v-if="recurring"
    >
      <v-card>
        <v-toolbar dark color="primary">
          <v-btn icon dark @click="dialogRecurring = false">
            <v-icon>close</v-icon>
          </v-btn>
          <v-toolbar-title>Edit recurring</v-toolbar-title>
        </v-toolbar>
        <v-card-text>
          <p>Edit this recurring cashflow entry</p>
          <v-form ref="recurringForm" v-model="validRecurring" lazy-validation>
            <DateDropdownHint label="Start date" v-model="recurring.timestamp" />
            <DateDropdownHint label="End date" v-model="recurring.end" />
            <v-text-field
              type="number"
              label="N period"
              v-model.number="recurring.nperiod"
              outline
              :rules="valueRules"
            />
            <v-select
              label="Period"
              v-model.number="recurring.periodid"
              :items="periods"
              item-value="id"
              item-text="value"
              outline
              :rules="valueRules"
              v-if="periods"
            />
            <v-select
              label="Sign"
              v-model.number="recurring.signid"
              :items="signs"
              item-value="id"
              item-text="value"
              outline
              v-if="signs"
            />
            <v-text-field
              type="number"
              label="Value"
              v-model.number="recurring.value"
              :rules="valueRules"
              outline
            />
            <v-select
              label="Purpose"
              v-model.number="recurring.purposeid"
              :items="purposes"
              item-value="id"
              item-text="body"
              outline
              :rules="valueRules"
              v-if="purposes"
            />
            <v-text-field label="Name" v-model="recurring.name" :rules="valueRules" outline />
            <v-btn @click="saveRecurring()">
              <v-icon>save</v-icon>Save
            </v-btn>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="dialogTransaction"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
      v-if="transaction"
    >
      <v-card>
        <v-toolbar dark color="primary">
          <v-btn icon dark @click="dialogTransaction = false">
            <v-icon>close</v-icon>
          </v-btn>
          <v-toolbar-title>Edit transaction</v-toolbar-title>
        </v-toolbar>
        <v-card-text>
          <p>Edit this transaction entry</p>
          <v-form ref="transactionForm" v-model="validTransaction" lazy-validation>
            <DateDropdownHint label="Date" v-model="transaction.timestamp" />
            <v-select
              label="Purpose"
              v-model.number="transaction.purposeid"
              :items="purposes"
              item-value="id"
              item-text="body"
              outline
              :rules="valueRules"
              v-if="purposes"
            />
            <v-select
              label="Sign"
              v-model.number="transaction.signid"
              :items="signs"
              item-value="id"
              item-text="value"
              outline
              v-if="signs"
            />
            <v-text-field
              type="number"
              label="Value"
              v-model.number="transaction.value"
              :rules="valueRules"
              outline
            />
            <v-btn @click="saveTransaction()">
              <v-icon>save</v-icon>Save
            </v-btn>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog v-model="loadingAccounts" persistent width="300">
      <v-card color="info" dark>
        <v-card-text>
          <span>Waiting for server to load accounts...</span>
          <v-progress-linear indeterminate color="white" class="mb-0"></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import CashflowSelect from "@/components/cashflow/CashflowSelect";
import DateDropdownHint from "@/components/date/DateDropdown";
import nbmlaw from "@/api/nbmlaw";
import { convertListOfObjectsToMap } from "@/util/helpers";
export default {
  name: "Cashflow",
  metaInfo() {
    return {
      title: "Cashflow",
      titleTemplate: "%s - Cashflow - Conveyancing Tracker",
      htmlAttrs: {
        lang: "en",
        amp: true
      }
    };
  },
  components: { CashflowSelect, DateDropdownHint },
  computed: {
    currenciesMap() {
      return this.currencies
        ? convertListOfObjectsToMap(this.currencies, "id")
        : undefined;
    },
    signsMap() {
      return this.signs
        ? convertListOfObjectsToMap(this.signs, "id")
        : undefined;
    },
    purposesMap() {
      return this.purposes
        ? convertListOfObjectsToMap(this.purposes, "id")
        : undefined;
    },
    periodsMap() {
      return this.periods
        ? convertListOfObjectsToMap(this.periods, "id")
        : undefined;
    },
    modifiedCashflow() {
      if (!this.cashflow) {
        return undefined;
      }
      const currency = this.currenciesMap
        ? this.currenciesMap[this.cashflow.currencyid]
        : undefined;
      return { ...this.cashflow, currency, accounts: this.modifiedAccounts };
    },
    modifiedAccounts() {
      if (!this.accounts) {
        return undefined;
      }
      const accounts = this.accounts
        .filter(a => !a.archived)
        .map(a => {
          const currency = this.currenciesMap
            ? this.currenciesMap[a.currencyid]
            : undefined;
          const balances = this.modifiedBalances
            ? this.modifiedBalances[a.id]
            : undefined;
          const recurrings = this.modifiedRecurrings
            ? this.modifiedRecurrings[a.id]
            : undefined;
          const transactions = this.modifiedTransactions
            ? this.modifiedTransactions[a.id]
            : undefined;
          return { ...a, currency, balances, recurrings, transactions };
        });
      return accounts;
    },
    modifiedBalances() {
      if (!this.balances) {
        return undefined;
      }
      const balances = new Object();
      for (const [key, value] of Object.entries(this.balances)) {
        const result = value.map(b => {
          const sign = this.signsMap ? this.signsMap[b.signid] : undefined;
          const valueOrder = b.signid == 0 ? -b.value : b.value;
          return { ...b, sign, valueOrder };
        });
        balances[key] = result;
      }
      return balances;
    },
    modifiedRecurrings() {
      if (!this.recurrings) {
        return undefined;
      }
      const recurrings = new Object();
      for (const [key, value] of Object.entries(this.recurrings)) {
        const result = value
          .filter(r => {
            if (this.showRecurringEnded) {
              return true;
            }
            if (!r.end) {
              return true;
            }
            if (new Date(r.end) < new Date()) {
              return false;
            }
            return true;
          })
          .map(r => {
            const sign = this.signsMap ? this.signsMap[r.signid] : undefined;
            const valueOrder = r.signid == 0 ? -r.value : r.value;
            const purpose = this.purposesMap
              ? this.purposesMap[r.purposeid]
              : undefined;
            const period = this.periodsMap
              ? this.periodsMap[r.periodid]
              : undefined;
            return { ...r, sign, valueOrder, purpose, period };
          });
        recurrings[key] = result;
      }
      return recurrings;
    },
    modifiedTransactions() {
      if (!this.transactions) {
        return undefined;
      }
      const transactions = new Object();
      for (const [key, value] of Object.entries(this.transactions)) {
        const result = value.map(t => {
          const sign = this.signsMap ? this.signsMap[t.signid] : undefined;
          const valueOrder = t.signid == 0 ? -t.value : t.value;
          const purpose = this.purposesMap
            ? this.purposesMap[t.purposeid]
            : undefined;
          return { ...t, sign, valueOrder, purpose };
        });
        transactions[key] = result;
      }
      return transactions;
    }
  },
  data() {
    return {
      cashflow: null,
      validCashflow: false,
      accounts: null,
      account: null,
      validAccount: false,
      balances: null,
      balance: null,
      validBalance: false,
      recurrings: null,
      recurring: null,
      validRecurring: false,
      transactions: null,
      transaction: null,
      validTransaction: false,
      currencies: null,
      periods: null,
      purposes: null,
      signs: null,
      loadingAccounts: false,
      showRecurringEnded: false,
      valueRules: [v => !!v || "Value is required"],
      headersBalance: [
        {
          text: "Date",
          value: "timestamp"
        },
        {
          text: "Value",
          value: "valueOrder",
          align: "right"
        },
        {
          text: " ",
          sortable: false
        }
      ],
      headersRecurring: [
        {
          text: "Start",
          value: "timestamp"
        },
        {
          text: "End",
          value: "end"
        },
        {
          text: "Value",
          value: "valueOrder",
          align: "right"
        },
        {
          text: "Purpose",
          value: "purpose.body"
        },
        {
          text: "Repeat",
          value: "timestamp"
        },
        {
          text: "Name",
          value: "name"
        },
        {
          text: " ",
          sortable: false
        }
      ],
      headersTransaction: [
        {
          text: "Date",
          value: "timestamp"
        },
        {
          text: "Purpose",
          value: "purpose.body"
        },
        {
          text: "Value",
          value: "valueOrder",
          align: "right"
        },
        {
          text: " ",
          sortable: false
        }
      ],
      dialogCashflow: false,
      dialogAccount: false,
      dialogBalance: false,
      dialogRecurring: false,
      dialogTransaction: false
    };
  },
  methods: {
    fetchCashflow() {
      nbmlaw
        .get(`/cors/admin/directory/entities/current/cashflows/current`)
        .then(response => {
          // console.log(response.data);
          this.cashflow = response.data;
        })
        .catch(() => {});
    },
    fetchAccounts() {
      this.loadingAccounts = true;
      nbmlaw
        .get(
          `/cors/admin/directory/entities/current/cashflows/current/accounts`
        )
        .then(response => {
          // console.log(response.data);
          this.accounts = response.data;
          this.loadingAccounts = false;
        })
        .catch(() => {
          this.loadingAccounts = false;
        });
    },
    fetchBalances() {
      nbmlaw
        .get(
          `/cors/admin/directory/entities/current/cashflows/current/balances`
        )
        .then(response => {
          // console.log(response.data);
          this.balances = response.data;
        })
        .catch(() => {});
    },
    fetchRecurrings() {
      nbmlaw
        .get(
          `/cors/admin/directory/entities/current/cashflows/current/recurrings`
        )
        .then(response => {
          // console.log(response.data);
          this.recurrings = response.data;
        })
        .catch(() => {});
    },
    fetchTransactions() {
      nbmlaw
        .get(
          `/cors/admin/directory/entities/current/cashflows/current/transactions`
        )
        .then(response => {
          // console.log(response.data);
          this.transactions = response.data;
        })
        .catch(() => {});
    },
    fetchCurrencies() {
      nbmlaw
        .get(`/cors/admin/cashflow/currencies`)
        .then(response => {
          // console.log(response.data);
          this.currencies = response.data;
        })
        .catch(() => {});
    },
    fetchPeriods() {
      nbmlaw
        .get(`/cors/admin/cashflow/periods`)
        .then(response => {
          // console.log(response.data);
          this.periods = response.data;
        })
        .catch(() => {});
    },
    fetchPurposes() {
      nbmlaw
        .get(`/cors/admin/cashflow/purposes`)
        .then(response => {
          // console.log(response.data);
          this.purposes = response.data;
        })
        .catch(() => {});
    },
    fetchSigns() {
      nbmlaw
        .get(`/cors/admin/cashflow/signs`)
        .then(response => {
          // console.log(response.data);
          this.signs = response.data;
        })
        .catch(() => {});
    },
    editCashflow() {
      this.dialogCashflow = true;
    },
    saveCashflow() {
      if (this.$refs.cashflowForm.validate()) {
        if (this.cashflow.id) {
          this.updateCashflow();
        } else {
          this.createCashflow();
        }
      }
    },
    createCashflow() {
      nbmlaw
        .post(`/cors/admin/cashflow/cashflows`, this.cashflow)
        .then(() => {
          // console.log(response.data);
          this.fetchAll();
          this.dialogCashflow = false;
        })
        .catch(() => {});
    },
    updateCashflow() {
      nbmlaw
        .put(
          `/cors/admin/cashflow/cashflows/${this.cashflow.id}`,
          this.cashflow
        )
        .then(() => {
          // console.log(response.data);
          this.fetchAll();
          this.dialogCashflow = false;
        })
        .catch(() => {});
    },
    deleteCashflow() {
      if (confirm("Are you sure?")) {
        nbmlaw
          .delete(`/cors/admin/cashflow/cashflows/${this.cashflow.id}`)
          .then(() => {
            // console.log(response.data);
            this.fetchAll();
          })
          .catch(() => {});
      }
    },
    editAccount(account) {
      this.account = account ? account : { cashflowid: this.cashflow.id };
      this.dialogAccount = true;
    },
    saveAccount() {
      if (this.$refs.accountForm.validate()) {
        if (this.account.id) {
          this.updateAccount();
        } else {
          this.createAccount();
        }
      }
    },
    createAccount() {
      nbmlaw
        .post(`/cors/admin/cashflow/accounts`, this.account)
        .then(() => {
          // console.log(response.data);
          this.fetchAll();
          this.dialogAccount = false;
        })
        .catch(() => {});
    },
    updateAccount() {
      nbmlaw
        .put(`/cors/admin/cashflow/accounts/${this.account.id}`, this.account)
        .then(() => {
          // console.log(response.data);
          this.fetchAll();
          this.dialogAccount = false;
        })
        .catch(() => {});
    },
    deleteAccount(account) {
      if (confirm("Are you sure?")) {
        nbmlaw
          .delete(`/cors/admin/cashflow/accounts/${account.id}`)
          .then(() => {
            // console.log(response.data);
            this.fetchAll();
          })
          .catch(() => {});
      }
    },
    editBalance(account, balance) {
      this.balance = balance
        ? balance
        : {
            accountid: account.id,
            signid: 0,
            timestamp: new Date().toISOString().slice(0, 10)
          };
      this.dialogBalance = true;
    },
    saveBalance() {
      if (this.$refs.balanceForm.validate()) {
        if (this.balance.id) {
          this.updateBalance();
        } else {
          this.createBalance();
        }
      }
    },
    createBalance() {
      nbmlaw
        .post(`/cors/admin/cashflow/balances`, this.balance)
        .then(() => {
          // console.log(response.data);
          this.fetchAll();
          this.dialogBalance = false;
        })
        .catch(() => {});
    },
    updateBalance() {
      nbmlaw
        .put(`/cors/admin/cashflow/balances/${this.balance.id}`, this.balance)
        .then(() => {
          // console.log(response.data);
          this.fetchAll();
          this.dialogBalance = false;
        })
        .catch(() => {});
    },
    deleteBalance(balance) {
      if (confirm("Are you sure?")) {
        nbmlaw
          .delete(`/cors/admin/cashflow/balances/${balance.id}`)
          .then(() => {
            // console.log(response.data);
            this.fetchAll();
          })
          .catch(() => {});
      }
    },
    editRecurring(account, recurring) {
      this.recurring = recurring
        ? recurring
        : {
            accountid: account.id,
            signid: 0,
            timestamp: new Date().toISOString().slice(0, 10)
          };
      this.dialogRecurring = true;
    },
    saveRecurring() {
      if (this.$refs.recurringForm.validate()) {
        if (this.recurring.id) {
          this.updateRecurring();
        } else {
          this.createRecurring();
        }
      }
    },
    createRecurring() {
      nbmlaw
        .post(`/cors/admin/cashflow/recurrings`, this.recurring)
        .then(() => {
          // console.log(response.data);
          this.fetchAll();
          this.dialogRecurring = false;
        })
        .catch(() => {});
    },
    updateRecurring() {
      nbmlaw
        .put(
          `/cors/admin/cashflow/recurrings/${this.recurring.id}`,
          this.recurring
        )
        .then(() => {
          // console.log(response.data);
          this.fetchAll();
          this.dialogRecurring = false;
        })
        .catch(() => {});
    },
    deleteRecurring(recurring) {
      if (confirm("Are you sure?")) {
        nbmlaw
          .delete(`/cors/admin/cashflow/recurrings/${recurring.id}`)
          .then(() => {
            // console.log(response.data);
            this.fetchAll();
          })
          .catch(() => {});
      }
    },
    editTransaction(account, transaction) {
      this.transaction = transaction
        ? transaction
        : {
            accountid: account.id,
            signid: 0,
            timestamp: new Date().toISOString().slice(0, 10)
          };
      this.dialogTransaction = true;
    },
    saveTransaction() {
      if (this.$refs.transactionForm.validate()) {
        if (this.transaction.id) {
          this.updateTransaction();
        } else {
          this.createTransaction();
        }
      }
    },
    createTransaction() {
      nbmlaw
        .post(`/cors/admin/cashflow/transactions`, this.transaction)
        .then(() => {
          // console.log(response.data);
          this.fetchAll();
          this.dialogTransaction = false;
        })
        .catch(() => {});
    },
    updateTransaction() {
      nbmlaw
        .put(
          `/cors/admin/cashflow/transactions/${this.transaction.id}`,
          this.transaction
        )
        .then(() => {
          // console.log(response.data);
          this.fetchAll();
          this.dialogTransaction = false;
        })
        .catch(() => {});
    },
    deleteTransaction(transaction) {
      if (confirm("Are you sure?")) {
        nbmlaw
          .delete(`/cors/admin/cashflow/transactions/${transaction.id}`)
          .then(() => {
            // console.log(response.data);
            this.fetchAll();
          })
          .catch(() => {});
      }
    },
    updated() {
      this.fetchAll();
    },
    fetchAll() {
      this.fetchCashflow();
      this.fetchAccounts();
      this.fetchBalances();
      this.fetchRecurrings();
      this.fetchTransactions();
      this.fetchCurrencies();
      this.fetchPeriods();
      this.fetchPurposes();
      this.fetchSigns();
    }
  },
  created() {
    this.fetchAll();
  }
};
</script>

<style >
.cashflowCell {
  text-align: right;
}
.cashflowNegative {
  color: red;
}
</style>
